import { ReactNode } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import { Fund, UnitBatch } from '../../api/types'
import { reconstructedAmount } from '../../calc/preview'
import { allInvariantsPassed } from '../../calc/invariants'
import { SectionCard } from '../../components/SectionCard'
import { StatusChip } from '../../components/StatusChip'
import { tokens } from '../../theme'
import { formatCzk, formatDate, formatDateTime, formatPercent, formatUnits } from '../jednotky/format'

/** Malý odznak „override". */
function OverrideChip() {
  return (
    <Box
      component="span"
      sx={{
        ml: 0.75,
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '9999px',
        fontWeight: 600,
        fontSize: '0.6875rem',
        px: '6px',
        py: '1px',
        bgcolor: tokens.warning[100],
        color: tokens.warning[700],
      }}
    >
      override
    </Box>
  )
}

function SummaryRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ pt: '2px' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </>
  )
}

interface BatchReviewPanelProps {
  batch: UnitBatch
  fund: Fund
  /** Odchylka NAV proti minulému ocenění (nebo null = první ocenění). */
  navDeviationRatio: number | null
  isCeo: boolean
  /** Kontrola (testy + AI) právě běží. */
  checking: boolean
  /** Potvrzení/zamítnutí právě probíhá. */
  submitting: boolean
  onDownloadReport: () => void
  /** Znovu spustit automatické testy + AI (AI je placená, proto na tlačítko). */
  onRecheck: () => void
  onConfirmClick: () => void
  onRejectClick: () => void
}

/**
 * Náhled kontroly přepočtu (fáze 5): dashboard (účastníci, nárůst jednotek, cena,
 * změna NAV), automatické testy, AI kontrola a akce POTVRDIT/ZAMÍTNOUT.
 * POTVRDIT/ZAMÍTNOUT vidí a spouští jen CEO; ostatní Apex mají náhled read-only
 * (rozhodnutí F5-A). POTVRDIT je zablokováno, dokud neprojdou všechny invarianty.
 */
export function BatchReviewPanel({
  batch,
  fund,
  navDeviationRatio,
  isCeo,
  checking,
  submitting,
  onDownloadReport,
  onRecheck,
  onConfirmClick,
  onRejectClick,
}: BatchReviewPanelProps) {
  const sigma = batch.totalUnitsAfter - batch.totalUnitsBefore
  const autoTests = batch.check?.autoTests ?? []
  const aiResults = batch.check?.aiResults ?? []
  const allPassed = allInvariantsPassed(autoTests)
  const hasChecks = autoTests.length > 0

  const deviationText = navDeviationRatio === null ? 'první ocenění' : formatPercent(navDeviationRatio)

  return (
    <SectionCard
      title={`Kontrola přepočtu ${batch.id} — ${fund.key}`}
      action={<StatusChip status={batch.status} />}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Dashboard: souhrn */}
        <Box
          component="dl"
          sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 3, rowGap: 1 }}
        >
          <SummaryRow label="Datum přidělení" value={formatDate(batch.assignmentDate)} />
          <SummaryRow label="Cena jednotky" value={formatUnits(batch.unitPrice)} />
          <SummaryRow label="NAV" value={formatCzk(batch.navTotal)} />
          <SummaryRow
            label="Jednotky (před → po)"
            value={`${formatUnits(batch.totalUnitsBefore)} → ${formatUnits(batch.totalUnitsAfter)} (+${formatUnits(sigma)})`}
          />
          <SummaryRow label="Změna NAV vs. minulé ocenění" value={deviationText} />
          <SummaryRow
            label="Vytvořil"
            value={`${batch.createdBy} · ${formatDateTime(batch.createdAt)}`}
          />
        </Box>

        {/* Dashboard: účastníci */}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Klient</TableCell>
                <TableCell align="right">Vklad</TableCell>
                <TableCell align="right">Cena</TableCell>
                <TableCell align="right">Jednotky</TableCell>
                <TableCell align="right">Kontrola (cena × jednotky)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batch.participants.map((p) => {
                const price = p.priceOverride ?? batch.unitPrice
                return (
                  <TableRow key={p.depositId}>
                    <TableCell>{p.clientName}</TableCell>
                    <TableCell align="right">{formatCzk(p.amount)}</TableCell>
                    <TableCell align="right">
                      {formatUnits(price)}
                      {p.priceOverride != null && <OverrideChip />}
                    </TableCell>
                    <TableCell align="right">
                      {formatUnits(p.units)}
                      {p.unitsOverride != null && <OverrideChip />}
                    </TableCell>
                    <TableCell align="right">
                      {formatCzk(reconstructedAmount(p.units, price))}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Box>

        {checking ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Probíhá kontrola (automatické testy + AI)…
            </Typography>
          </Box>
        ) : (
          <>
            {/* Automatické testy */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Automatické testy
                </Typography>
                {hasChecks && (
                  <Box
                    component="span"
                    sx={{
                      borderRadius: '9999px',
                      px: '10px',
                      py: '2px',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      bgcolor: allPassed ? tokens.success[100] : tokens.error[100],
                      color: allPassed ? tokens.success[700] : tokens.error[700],
                    }}
                  >
                    {allPassed ? 'VŠE OK' : 'NALEZENY CHYBY'}
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {autoTests.map((test) => (
                  <Box
                    key={test.name}
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                  >
                    {test.passed ? (
                      <CheckCircleOutlineIcon
                        fontSize="small"
                        sx={{ color: tokens.success[500], mt: '2px' }}
                      />
                    ) : (
                      <CancelOutlinedIcon fontSize="small" sx={{ color: tokens.error[500], mt: '2px' }} />
                    )}
                    <Box>
                      <Typography variant="body2">{test.name}</Typography>
                      {test.detail && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          {test.detail}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* AI kontrola */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                AI kontrola
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {aiResults.map((result) => {
                  const name =
                    batch.participants.find((p) => p.clientId === result.id)?.clientName ??
                    result.name
                  return (
                    <Box key={result.id}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {name} —{' '}
                        <Box
                          component="span"
                          sx={{
                            color: result.status === 'OK' ? tokens.success[700] : tokens.error[700],
                          }}
                        >
                          {result.status === 'OK' ? 'V POŘÁDKU' : 'CHYBA'}
                        </Box>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="div">
                        {result.comment}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>
            </Box>

            {!hasChecks && (
              <Alert severity="warning">
                Kontrola neproběhla (automatické testy nedorazily) — potvrzení je zablokováno.
                Spusť kontrolu znovu.
              </Alert>
            )}

            {!allPassed && hasChecks && (
              <Alert severity="error">
                Přepočet neprošel automatickými testy — potvrzení je zablokováno. Zkontroluj batch nebo
                jej zamítni.
              </Alert>
            )}
          </>
        )}

        {/* Akce */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={onDownloadReport}
            disabled={submitting}
          >
            Stáhnout report
          </Button>
          <Button variant="outlined" onClick={onRecheck} disabled={submitting || checking}>
            Spustit kontrolu znovu
          </Button>
          {isCeo ? (
            <>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="outlined"
                color="error"
                onClick={onRejectClick}
                disabled={submitting || checking}
              >
                Zamítnout
              </Button>
              <Button
                variant="contained"
                onClick={onConfirmClick}
                disabled={submitting || checking || !allPassed}
              >
                Potvrdit
              </Button>
            </>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Potvrzení a zamítnutí přepočtu provádí CEO.
            </Typography>
          )}
        </Box>
      </Box>
    </SectionCard>
  )
}
